import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Send, MessageCircle, ArrowLeft, Phone, MapPin, User, DollarSign, Tag, X, ClipboardCopy } from 'lucide-react';
import { formatPrice, brandifyText } from '../lib/utils';
import { SITE_NAME } from '../lib/brand';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface Message {
  id: number;
  phone: string;
  message: string;
  sender: 'user' | 'bot';
  createdAt: number;
}

interface Order {
  id: number;
  createdAt: number;
  paymentConfirmed: number;
  paymentProof?: string;
  uploads?: any[];
  name: string;
  phone: string;
  region: string;
  address: string;
  items?: string;
  totalPrice: number;
  remarks?: string;
  adminRemarks?: string;
  orderCompleted: number;
  [key: string]: any;
}

const DELIVERY_REGION_LABELS: Record<string, string> = {
  HK: '香港島',
  KLN: '九龍',
  NT: '新界',
  OS: '海外',
};

const AdminOrderDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [customerTags, setCustomerTags] = useState<any[]>([]);
  const [customerOrderCount, setCustomerOrderCount] = useState(0);
  const [customerTotalSpent, setCustomerTotalSpent] = useState(0);
  const [productImages, setProductImages] = useState<Record<number, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const storedUser = localStorage.getItem('admin_user');
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchOrder();
    fetchProductImages();
  }, [id]);

  // Order items only store productId/name/qty/price — look up thumbnails from
  // the product catalog so the items list can render as horizontal rows.
  const fetchProductImages = async () => {
    try {
      const res = await apiFetch('/api/public/admin/products');
      if (!res.ok) return;
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data ?? json.products ?? []);
      const map: Record<number, string> = {};
      list.forEach((p: any) => {
        const img = p.imageUrl || p.image_url;
        if (p.id && img) map[Number(p.id)] = String(img);
      });
      setProductImages(map);
    } catch {
      // Thumbnails are cosmetic — keep the page working without them.
    }
  };

  useEffect(() => {
    if (order?.phone) {
      fetchChatHistory(order.phone);
      fetchCustomerTags(order.phone);
    }
  }, [order]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchOrder = async () => {
    const res = await apiFetch(`/api/public/admin/orders/${id}`);
    if (res.ok) {
      const json = await res.json();
      setOrder(json.data ?? json);
    }
    else if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); }
    setLoading(false);
  };

  const fetchChatHistory = async (phone: string) => {
    setChatLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/whatsapp/conversations/${phone}`);
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        setMessages(
          (data.messages || []).map((m: any) => ({
            ...m,
            sender: m.direction === 'outbound' || m.sender === 'bot' ? 'bot' : 'user',
          }))
        );
      }
    } catch (e) { console.error('Failed to fetch chat:', e); }
    finally { setChatLoading(false); }
  };

  const fetchCustomerTags = async (phone: string) => {
    try {
      const res = await apiFetch(`/api/public/admin/customers/${phone}`);
      if (res.ok) {
        const data = await res.json();
        setCustomerTags(data.manualTags || []);
        setCustomerOrderCount((data.orders || []).length);
        setCustomerTotalSpent(
          (data.orders || []).reduce((sum: number, o: any) => sum + (o.total_price || 0), 0)
        );
      }
    } catch (e) { console.error('Failed to fetch customer tags:', e); }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await apiFetch(`/api/public/admin/orders/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(order)
    });
    if (res.ok) { alert('更新成功'); navigate('/admin/orders'); }
    else { alert('更新失敗'); }
  };

  const handleConfirmPayment = async () => {
    if (!confirm('確認已收到此訂單的付款？')) return;
    setConfirming(true);
    const updatedOrder = { ...order, paymentConfirmed: 1 };
    const res = await apiFetch(`/api/public/admin/orders/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatedOrder)
    });
    if (res.ok) {
      setOrder({ ...order, paymentConfirmed: 1 });
      alert('付款已確認！客戶將收到 WhatsApp 通知。');
    } else { alert('確認失敗'); }
    setConfirming(false);
  };

  const handleCompleteOrder = async () => {
    if (!order) return;
    if (!confirm('確認標記此訂單為已完成？')) return;
    setCompleting(true);
    const res = await apiFetch(`/api/public/admin/orders/${order.id}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    if (res.ok) {
      alert('訂單已標記為完成');
      await fetchOrder();
    } else {
      alert('標記失敗');
    }
    setCompleting(false);
  };

  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [resendingCompletion, setResendingCompletion] = useState(false);

  // 出口人工審核（客人 2026-09-05：海外收貨訂單落單即 pending）
  const handleExportReview = async (action: 'approve' | 'reject') => {
    if (!order) return;
    let note = '';
    if (action === 'approve') {
      if (!confirm('確認通過出口審核？訂單將繼續現行處理流程。')) return;
    } else {
      const input = prompt('退回原因（會 WhatsApp 通知客人，可留空）：');
      if (input === null) return;
      note = input.trim();
    }
    const res = await apiFetch(`/api/public/admin/orders/${order.id}/export-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, note })
    });
    if (res.ok) {
      alert(action === 'approve' ? '已通過出口審核' : '已退回並取消訂單');
      await fetchOrder();
    } else {
      const data = await res.json().catch(() => ({}));
      alert(data.error || '操作失敗');
    }
  };

  const handleSendMessage = async () => {
    if (!order?.phone || !replyText.trim()) return;
    setSendLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: order.phone, message: replyText.trim() })
      });
      if (res.ok) {
        setReplyText('');
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || '發送失敗');
      }
      // Always refresh so the outbound message (sent or failed) appears in the chat
      await fetchChatHistory(order.phone);
    } catch (e) {
      alert('發送失敗');
      await fetchChatHistory(order.phone);
    }
    finally { setSendLoading(false); }
  };

  const handleResendConfirmation = async () => {
    if (!order?.id) return;
    if (!confirm('重新發送訂單確認 WhatsApp 訊息給客戶？')) return;
    setResendingConfirmation(true);
    try {
      const res = await apiFetch(`/api/public/admin/orders/${order.id}/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        alert('確認訊息已重新發送');
        if (order?.phone) await fetchChatHistory(order.phone);
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || '發送失敗');
      }
    } catch (e) {
      alert('發送失敗');
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleResendCompletion = async () => {
    if (!order?.id) return;
    if (!confirm('重新發送訂單完成 WhatsApp 訊息給客戶？')) return;
    setResendingCompletion(true);
    try {
      const res = await apiFetch(`/api/public/admin/orders/${order.id}/resend-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        alert('完成訊息已重新發送');
        if (order?.phone) await fetchChatHistory(order.phone);
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || '發送失敗');
      }
    } catch (e) {
      alert('發送失敗');
    } finally {
      setResendingCompletion(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  if (loading) return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">加載中...</div>
    </AdminLayout>
  );
  if (!order) return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      <div className="text-center text-gray-500 dark:text-gray-400 py-12">訂單不存在</div>
    </AdminLayout>
  );

  // The admin API already parses items via parseOrderRow (array), while older
  // payloads / fallbacks may still be a JSON string — handle both.
  const orderItems: any[] = (() => {
    try {
      const raw: any = order.items;
      if (Array.isArray(raw)) return raw;
      return JSON.parse(raw || '[]');
    } catch { return []; }
  })();

  // 一按複製：純文字訂單摘要（WhatsApp 轉發第三者用，例：供貨商／物流）
  const buildOrderSummaryText = (): string => {
    if (!order) return '';
    const statusLabel = order.orderCompleted === 1
      ? '訂單完成'
      : order.paymentConfirmed === 1
        ? '已付款 / 處理中'
        : '待付款';
    const lines: string[] = [];
    lines.push(`【${SITE_NAME} 訂單 ${order.orderNum || ''}】`);
    lines.push(`客戶：${order.name || '-'}`);
    lines.push(`電話：${order.phone || '-'}`);
    lines.push(`落單時間：${new Date(order.createdAt * 1000).toLocaleString('zh-HK')}`);
    lines.push(`狀態：${statusLabel}`);
    if (order.paymentMethod) lines.push(`付款方式：${order.paymentMethod === 'fps' ? 'FPS 轉數快' : order.paymentMethod === 'payme' ? 'PayMe' : order.paymentMethod}`);
    lines.push('');
    lines.push('貨品：');
    for (const it of orderItems) {
      const qty = Number(it.quantity || 1);
      const subtotal = Number(it.subtotal ?? (Number(it.unitPrice || 0) * qty));
      lines.push(`• ${brandifyText(String(it.name || ''))} ×${qty} — ${formatPrice(subtotal)}`);
    }
    lines.push('');
    lines.push(`貨品總額：${formatPrice(order.totalPrice || 0)}`);
    const discount = Number(order.discountAmount || 0);
    if (discount > 0) lines.push(`折扣：-${formatPrice(discount)}`);
    lines.push(`應付總額：${formatPrice(order.finalPrice ?? order.totalPrice ?? 0)}`);
    lines.push('');
    lines.push(`送貨地區：${DELIVERY_REGION_LABELS[order.region] || order.region || '-'}`);
    lines.push(`地址：${order.address || '-'}`);
    if (order.deliveryRemark) lines.push(`送貨備註：${order.deliveryRemark}`);
    if (order.remarks) lines.push(`客戶備註：${order.remarks}`);
    return lines.join('\n');
  };

  const copyOrderSummary = async () => {
    const text = buildOrderSummaryText();
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // 舊瀏覽器／非安全環境 fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    // 非阻塞反饋（alert 會凍結頁面，自動化測試也會卡死）
    setCopied(ok ? 'ok' : 'fail');
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin/orders')} className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> 返回訂單列表
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">訂單 #{(order.orderNum || "-")}</h1>
          <button
            onClick={copyOrderSummary}
            title="複製訂單摘要到剪貼簿，方便貼上 WhatsApp 轉發"
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border transition ${
              copied === 'ok'
                ? 'bg-green-600 text-white border-green-600'
                : copied === 'fail'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-red-600 hover:text-red-600'
            }`}
          >
            <ClipboardCopy className="w-4 h-4" />
            {copied === 'ok' ? '已複製 ✓' : copied === 'fail' ? '複製失敗，請重試' : '複製訂單資料'}
          </button>
          {order.orderCompleted === 1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">訂單完成</span>
          ) : order.paymentConfirmed === 1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">已付款 / 處理中</span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">待付款</span>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">下單時間：{new Date(order.createdAt * 1000).toLocaleString('zh-HK')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: Order Info (3/5) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Payment Proof */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              付款記錄
            </h2>
            {order.paymentProof && (!order.uploads || order.uploads.length === 0) && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">付款憑證：</p>
                <a href={order.paymentProof} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline break-all text-sm">
                  {order.paymentProof}
                </a>
              </div>
            )}
            {order.uploads && order.uploads.length > 0 ? (
              <div className="grid grid-cols-4 gap-3 mb-4">
                {order.uploads.map((u: any) => (
                  <div key={u.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {u.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <a href={u.fileUrl} target="_blank" rel="noopener noreferrer">
                        <img src={u.fileUrl} alt="Proof" className="w-full h-24 object-cover hover:opacity-90" />
                      </a>
                    ) : (
                      <a href={u.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-24 text-red-600 text-xs bg-gray-50 dark:bg-gray-900">
                        查看文件
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : !order.paymentProof && <p className="text-gray-400 dark:text-gray-500 text-sm italic mb-4">暫無付款憑證</p>}

            <div className="flex flex-col gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    checked={order.paymentConfirmed === 1}
                    disabled={confirming}
                    onChange={async (e) => {
                      const newValue = e.target.checked ? 1 : 0;
                      if (newValue === 1 && !confirm('確認已收到付款？系統將發送 WhatsApp 通知給客戶。')) return;
                      setConfirming(true);
                      const updatedOrder = { ...order, paymentConfirmed: newValue };
                      const res = await apiFetch(`/api/public/admin/orders/${id}`, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatedOrder)
                      });
                      if (res.ok) { setOrder({...order, paymentConfirmed: newValue}); }
                      else { alert('更新失敗'); e.target.checked = !e.target.checked; }
                      setConfirming(false);
                    }}
                  />
                  <span className={`font-bold ${order.paymentConfirmed ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}`}>
                    {order.paymentConfirmed ? '已確認付款' : '未確認付款'}
                  </span>
                </label>
                {!order.paymentConfirmed && (
                  <button
                    onClick={handleConfirmPayment}
                    disabled={confirming}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {confirming ? '處理中...' : '一鍵確認付款'}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={handleResendConfirmation}
                  disabled={resendingConfirmation}
                  className="text-xs bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {resendingConfirmation ? '發送中...' : '重發確認 WhatsApp'}
                </button>
              </div>

            </div>
          </div>

          {/* 出口人工審核（客人 2026-09-05：海外收貨訂單一律待審核） */}
          {order.region === 'OS' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">🌏 出口審核</h2>
              {order.exportReviewStatus === 'pending' ? (
                <>
                  <p className="text-sm text-orange-600 font-bold mb-3">⏳ 待審核——海外收貨訂單需人工確認可出口後再處理。</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleExportReview('approve')}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 transition-colors"
                    >
                      通過審核
                    </button>
                    <button
                      onClick={() => handleExportReview('reject')}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
                    >
                      退回（取消訂單）
                    </button>
                  </div>
                </>
              ) : order.exportReviewStatus === 'approved' ? (
                <p className="text-sm text-green-600 font-bold">✅ 已通過出口審核{order.exportReviewNote ? `——${order.exportReviewNote}` : ''}</p>
              ) : order.exportReviewStatus === 'rejected' ? (
                <p className="text-sm text-red-600 font-bold">⛔ 已退回（訂單已取消）{order.exportReviewNote ? `——${order.exportReviewNote}` : ''}</p>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">此訂單早於出口審核機制上線，未標記。</p>
              )}
            </div>
          )}

          {/* Order Info (Read Only) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">訂單資料（只讀）</h2>
              <button
                onClick={handleResendCompletion}
                disabled={resendingCompletion}
                className="text-xs bg-green-50 text-green-600 border border-green-200 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {resendingCompletion ? '發送中...' : '重發完成 WhatsApp'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> 姓名</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.name}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> 電話</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.phone}</div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> 地址</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{DELIVERY_REGION_LABELS[order.region] || order.region} {order.address}</div>
              </div>

            </div>

            {/* Customer Tags */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> 客戶標籤
                </label>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const tags: { label: string; color: string }[] = [];
                    if (customerOrderCount === 1) tags.push({ label: '新客戶', color: 'bg-blue-100 text-blue-700 border-blue-200' });
                    else if (customerOrderCount >= 2 && customerOrderCount < 5) tags.push({ label: '回頭客', color: 'bg-purple-100 text-purple-700 border-purple-200' });
                    if (customerOrderCount >= 5) tags.push({ label: 'VIP', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' });
                    if ((order.finalPrice || order.totalPrice) >= 50000) tags.push({ label: '大單', color: 'bg-amber-100 text-amber-700 border-amber-200' });
                    const computedLabels = tags.map(t => t.label);
                    const displayTags = [
                      ...tags,
                      ...customerTags
                        .filter((t: any) => !computedLabels.includes(t.tag))
                        .map((t: any) => ({ label: t.tag, color: `bg-${t.color}-100 text-${t.color}-700 border-${t.color}-200` })),
                    ];
                    if (displayTags.length === 0) {
                      return <span className="text-xs text-gray-400 italic">暫無標籤</span>;
                    }
                    return displayTags.map((t, i) => (
                      <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${t.color}`}>
                        {t.label}
                      </span>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Items — horizontal rows: thumbnail left, name/qty middle, subtotal right. Mobile-friendly full-width rows. */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">產品明細</label>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {orderItems.map((item: any, idx: number) => {
                  const itemName = brandifyText(String(item.name
                    ? item.name
                    : item.packageType
                      ? (item.packageType === '2-dish-1-soup' ? '2餸1湯' : '3餸1湯')
                      : (item.productId || '商品')));
                  const qty = item.quantity || 1;
                  const img = productImages[Number(item.productId)];
                  return (
                    <div key={idx} className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                      {img ? (
                        <img src={img} alt={String(itemName)} className="w-12 aspect-square rounded-lg object-cover flex-shrink-0 bg-white" />
                      ) : (
                        <div className="w-12 aspect-square rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-lg flex-shrink-0">📦</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words">{itemName}</div>
                        {item.unitPrice != null && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatPrice(item.unitPrice)} × {qty}</div>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">{formatPrice(item.subtotal || (item.unitPrice * qty))}</div>
                    </div>
                  );
                })}
                <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>小計</span>
                    <span>{formatPrice(order.totalPrice)}</span>
                  </div>
                  {order.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>優惠{order.giftCode ? ` (${order.giftCode})` : ''}</span>
                      <span>-{formatPrice(order.discountAmount)}</span>
                    </div>
                  )}
                  {(() => {
                    const deliveryFee = order.finalPrice - order.totalPrice + order.discountAmount;
                    if (deliveryFee <= 0) return null;
                    return (
                      <div className="flex justify-between">
                        <span>運費</span>
                        <span>{formatPrice(deliveryFee)}</span>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between font-bold pt-1">
                    <span>總計</span>
                    <span>{formatPrice(order.finalPrice)}</span>
                  </div>
                </div>
              </div>
            </div>


            {/* Customer Remarks (read-only) */}
            {order.remarks && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">客戶備註</label>
                <div className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {order.remarks}
                </div>
              </div>
            )}

            {/* Admin Editable Section */}
            <form onSubmit={handleUpdate} className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">管理員編輯區</h3>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1 text-red-500">管理員備註</label>
                <textarea className="w-full p-2.5 border border-red-200 bg-red-50 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none" rows={2} value={order.adminRemarks || ''} onChange={e => setOrder({...order, adminRemarks: e.target.value})} />
              </div>
              {order.paymentConfirmed === 1 && order.orderCompleted === 0 && isAdmin && (
                <div className="mb-4">
                <button
                  type="button"
                  onClick={handleCompleteOrder}
                  disabled={completing}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {completing ? '處理中...' : '標記訂單完成'}
                </button>
              </div>
              )}
              <div className="flex gap-2">
                <button type="submit" className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">儲存備註</button>
                <button type="button" onClick={() => navigate('/admin/orders')} className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">取消</button>
              </div>
            </form>
          </div>
        </div>

        {/* RIGHT: WhatsApp Chat (2/5) */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col h-[calc(100vh-180px)] sticky top-6">
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-red-500 to-red-600 rounded-t-xl">
              <div className="flex items-center gap-2 text-white">
                <MessageCircle className="w-5 h-5" />
                <div>
                  <h3 className="font-bold text-sm">客戶對話</h3>
                  <p className="text-xs text-red-100">{order.phone}</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
              {chatLoading ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-8 text-sm">加載對話中...</p>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 dark:text-gray-500 text-sm">暫無對話記錄</p>
                  <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">客戶發送消息後會自動顯示在此</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender === 'bot' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                      msg.sender === 'bot'
                        ? 'bg-red-600 text-white rounded-br-none'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-300 rounded-bl-none'
                    }`}>
                      <div className="break-words">{msg.message}</div>
                      <div className={`text-xs mt-1 ${msg.sender === 'bot' ? 'text-red-200' : 'text-gray-400'}`}>
                        {msg.createdAt ? new Date(msg.createdAt * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Send Log */}
            {messages.filter(m => m.sender === 'bot').length > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs text-gray-400">
                  已發送 {messages.filter(m => m.sender === 'bot').length} 條消息 ·
                  最後發送：{
                    (() => {
                      const lastBot = messages.filter(m => m.sender === 'bot').pop();
                      return lastBot?.createdAt
                        ? new Date(lastBot.createdAt * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '-';
                    })()
                  }
                </p>
              </div>
            )}

            {/* Reply Input */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-xl">
              <div className="flex gap-2">
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="輸入消息通知客戶..."
                  rows={2}
                  className="flex-1 p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none bg-white dark:bg-gray-800 dark:text-white"
                  disabled={sendLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={sendLoading || !replyText.trim()}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-1"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">按 Enter 發送 · Shift+Enter 換行</p>
            </div>
          </div>
        </div>
      </div>


    </AdminLayout>
  );
};

export default AdminOrderDetail;
